import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { Loader } from "@/components/ui/Loader";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  value?: string[] | null;
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  /**
   * Máximo de chips visíveis no gatilho.
   * Se omitido, calcula automaticamente o que cabe em até 2 linhas e mostra "+N".
   */
  maxDisplay?: number;
  /** Largura mínima do painel (px); pode ser maior que o gatilho para ver labels completos. */
  dropdownMinWidth?: number;
  /** Impede que cliques no painel fechem componentes pais (ex.: tabela). */
  stopPropagation?: boolean;
}

/** Altura útil de ~2 linhas de chips (chip ~22px + gap). */
const CHIP_AREA_MAX_ROWS = 2;
const CHIP_GAP_PX = 6;

function countChipsFittingInRows(
  chipWidths: number[],
  overflowWidth: number,
  containerWidth: number,
  gap: number,
  maxRows: number,
): number {
  if (chipWidths.length === 0 || containerWidth <= 0) return 0;

  for (let n = chipWidths.length; n >= 0; n--) {
    const widths = chipWidths.slice(0, n);
    if (n < chipWidths.length) widths.push(overflowWidth);

    let row = 1;
    let rowWidth = 0;
    let fits = true;
    for (const w of widths) {
      const next = rowWidth === 0 ? w : rowWidth + gap + w;
      if (next <= containerWidth + 0.5) {
        rowWidth = next;
      } else {
        row += 1;
        rowWidth = w;
        if (row > maxRows) {
          fits = false;
          break;
        }
      }
    }
    if (fits) return n;
  }
  return 0;
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
export function MultiSelect({
  value: valueProp,
  onChange,
  options,
  placeholder = "Todos",
  label,
  loading = false,
  disabled = false,
  className,
  id,
  maxDisplay,
  dropdownMinWidth,
  stopPropagation = true,
}: MultiSelectProps) {
  const value = Array.isArray(valueProp) ? valueProp : [];

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 320 });
  const [visibleCount, setVisibleCount] = useState(value.length);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);

  const inputId = id ?? `multiselect-${label?.toLowerCase().replace(/\s+/g, "-") ?? "cc"}`;

  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  const chipLabel = (v: string) => labelByValue.get(v) ?? v;

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minW = dropdownMinWidth ?? 0;
    let width = Math.max(rect.width, minW);
    width = Math.min(width, vw - 2 * margin);
    let left = rect.left;
    if (left + width > vw - margin) left = vw - margin - width;
    if (left < margin) left = margin;

    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const preferredHeight = 360;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(preferredHeight, openUp ? spaceAbove - 8 : spaceBelow - 8, 420);

    setDropdownPos({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left,
      width,
      maxHeight: Math.max(maxHeight, 160),
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, dropdownMinWidth]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, dropdownMinWidth]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
  }, [open]);

  useLayoutEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const syncWidth = () => setTriggerWidth(el.clientWidth);
    syncWidth();
    const ro = new ResizeObserver(syncWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (typeof maxDisplay === "number" && Number.isFinite(maxDisplay) && maxDisplay >= 0) {
      setVisibleCount(Math.min(Math.floor(maxDisplay), value.length));
      return;
    }

    if (value.length === 0) {
      setVisibleCount(0);
      return;
    }

    const sizer = sizerRef.current;
    if (!sizer || triggerWidth <= 0) {
      setVisibleCount(value.length);
      return;
    }

    const chipEls = Array.from(sizer.querySelectorAll<HTMLElement>("[data-sizer-chip]"));
    const overflowEl = sizer.querySelector<HTMLElement>("[data-sizer-overflow]");
    const chipWidths = chipEls.map((c) => c.getBoundingClientRect().width);
    const overflowWidth = overflowEl?.getBoundingClientRect().width ?? 40;
    const contentWidth = Math.max(triggerWidth - 56, 40);

    const fitted = countChipsFittingInRows(
      chipWidths,
      overflowWidth,
      contentWidth,
      CHIP_GAP_PX,
      CHIP_AREA_MAX_ROWS,
    );
    setVisibleCount(fitted);
  }, [value, labelByValue, maxDisplay, triggerWidth]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((o) => value.includes(o.value));

  const toggleOption = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      onChange(value.filter((v) => !filtered.some((o) => o.value === v)));
    } else {
      const toAdd = filtered.map((o) => o.value).filter((v) => !value.includes(v));
      onChange([...value, ...toAdd]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const removeOne = (e: React.MouseEvent, v: string) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((v) => !v);
    }
  };

  const handleDropdownKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
      triggerRef.current?.focus();
    }
  };

  const hasSelection = value.length > 0;
  const listMaxHeight = Math.max(dropdownPos.maxHeight - 120, 120);
  const safeVisible = Math.max(0, Math.min(visibleCount, value.length));
  const visibleValues = value.slice(0, safeVisible);
  const hiddenValues = value.slice(safeVisible);
  const hiddenCount = hiddenValues.length;
  const hiddenTitle = hiddenValues.map(chipLabel).join("\n");

  const chipClassName =
    "inline-flex max-w-[9rem] items-center gap-1 rounded-md border border-brand-100 bg-white px-2 py-0.5 text-xs font-medium text-brand-900";

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          onKeyDown={handleDropdownKeyDown}
          onMouseDown={(e) => {
            if (stopPropagation) e.stopPropagation();
          }}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight,
            zIndex: 9999,
            transform: dropdownPos.top < (triggerRef.current?.getBoundingClientRect().top ?? 0)
              ? "translateY(-100%)"
              : undefined,
          }}
          className="bg-white rounded-xl border border-slate-200 shadow-modal overflow-hidden flex flex-col"
          role="listbox"
          aria-multiselectable="true"
        >
          <div className="p-2 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar..."
                className="w-full h-8 pl-8 pr-8 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  type="button"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {filtered.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide hover:bg-slate-50 border-b border-slate-100 transition-colors shrink-0"
            >
              <div
                className={cn(
                  "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                  allFilteredSelected ? "bg-brand-600 border-brand-600" : "border-slate-300",
                )}
              >
                {allFilteredSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </div>
              {allFilteredSelected ? "Desmarcar todos" : "Selecionar todos"}
            </button>
          )}

          <ul className="overflow-y-auto py-1 flex-1 min-h-0" style={{ maxHeight: listMaxHeight }} role="listbox">
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-slate-400 text-center">Nenhum resultado encontrado.</li>
            ) : (
              filtered.map((option) => {
                const selected = value.includes(option.value);
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={selected}
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "flex items-start gap-2.5 px-3 py-2 text-sm cursor-pointer select-none transition-colors",
                      selected ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-50",
                    )}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                        selected ? "bg-brand-600 border-brand-600" : "border-slate-300",
                      )}
                    >
                      {selected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </div>
                    <span className="min-w-0 flex-1 break-words leading-snug">{option.label}</span>
                  </li>
                );
              })
            )}
          </ul>

          {value.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between shrink-0">
              <span className="text-xs text-slate-500">
                {value.length} selecionado{value.length !== 1 ? "s" : ""}
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
              >
                Limpar tudo
              </button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={cn("flex flex-col gap-1.5 min-w-0", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
        >
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        id={inputId}
        type="button"
        disabled={disabled || loading}
        onClick={() => {
          if (!disabled && !loading) setOpen((v) => !v);
        }}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          "relative w-full min-h-10 rounded-lg border bg-slate-50 px-3 py-2 text-sm text-left",
          "flex items-start gap-2 transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600",
          open ? "border-brand-600 ring-2 ring-brand-600" : "border-slate-200 hover:border-slate-300",
          disabled || loading ? "opacity-60 cursor-not-allowed" : undefined,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {/* Sizer invisível: mede largura real dos chips para caber em até 2 linhas */}
        {hasSelection && (
          <div
            ref={sizerRef}
            aria-hidden
            className="pointer-events-none absolute left-3 right-12 top-0 -z-10 h-0 overflow-hidden opacity-0"
          >
            <div className="flex flex-wrap gap-1.5" style={{ width: Math.max(triggerWidth - 56, 40) }}>
              {value.map((v) => (
                <span key={v} data-sizer-chip className={chipClassName}>
                  <span className="truncate leading-snug">{chipLabel(v)}</span>
                  <X className="w-3 h-3 shrink-0" />
                </span>
              ))}
              <span data-sizer-overflow className={cn(chipClassName, "max-w-none tabular-nums")}>
                +{value.length}
              </span>
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {loading ? (
            <span className="text-slate-400">Carregando...</span>
          ) : !hasSelection ? (
            <span className="text-slate-400 leading-6">{placeholder}</span>
          ) : (
            <div className="flex max-h-[3.25rem] flex-wrap content-start gap-1.5 overflow-hidden">
              {visibleValues.map((v) => {
                const text = chipLabel(v);
                return (
                  <span key={v} className={chipClassName} title={text}>
                    <span className="truncate leading-snug">{text}</span>
                    {!disabled && !loading && (
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={`Remover ${text}`}
                        onClick={(e) => removeOne(e, v)}
                        className="shrink-0 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </span>
                    )}
                  </span>
                );
              })}
              {hiddenCount > 0 && (
                <span
                  className={cn(chipClassName, "max-w-none tabular-nums text-slate-600")}
                  title={hiddenTitle}
                >
                  +{hiddenCount}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
          {hasSelection && !loading && !disabled && (
            <span
              role="button"
              title="Limpar seleção"
              onClick={clearAll}
              className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer p-0.5 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}

          {loading ? (
            <Loader size="xs" color="slate" />
          ) : (
            <ChevronDown
              className={cn(
                "w-4 h-4 text-slate-400 transition-transform duration-150",
                open ? "rotate-180" : undefined,
              )}
            />
          )}
        </div>
      </button>

      {dropdown}
    </div>
  );
}
